import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  CardBody,
  CardTitle,
  Input,
  Button,
  Row,
  Col,
} from "reactstrap";
import DataTable from "react-data-table-component";
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import {
  getAPIURL,
  getVendorID,
  getFormattedPrice,
} from "../../services/helper";
import http from "../../services/http";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const PaymentType = () => {
  const api_url = getAPIURL();
  const vendorId = getVendorID();
  const [data, setData] = useState({ summary: {}, payment_types: [] });
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1))
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchPaymentTypeReport();
  }, []);

  const fetchPaymentTypeReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        vendor_id: vendorId,
        start_date: dateRange.start,
        end_date: dateRange.end,
      });

      const { data } = await http.get(
        `${api_url}/reports/sales/payment_type?${params.toString()}`
      );
      setData(data);
    } catch (error) {
      console.error("Error fetching payment type report:", error);
    }
    setLoading(false);
  };

  const columns = [
    {
      name: "Payment Type",
      selector: (row) => row.payment_type_name,
      sortable: true,
    },
    { name: "Total Sales", selector: (row) => row.total_sales, sortable: true },
    {
      name: "Total Amount",
      selector: (row) => getFormattedPrice(row.total_amount),
      sortable: true,
    },
    {
      name: "Total Paid",
      selector: (row) => getFormattedPrice(row.total_paid),
      sortable: true,
    },
    {
      name: "Total Tax",
      selector: (row) => getFormattedPrice(row.total_tax),
      sortable: true,
    },
    {
      name: "Change Given",
      selector: (row) => getFormattedPrice(row.total_change_given),
      sortable: true,
    },
    {
      name: "Transactions",
      selector: (row) => row.transaction_count,
      sortable: true,
    },
  ];

  const pieChartData = {
    labels: data.payment_types.map((pt) => pt.payment_type_name),
    datasets: [
      {
        data: data.payment_types.map((pt) => pt.total_amount),
        backgroundColor: [
          "#FF6384",
          "#36A2EB",
          "#FFCE56",
          "#4BC0C0",
          "#9966FF",
          "#FF9F40",
        ],
      },
    ],
  };

  const barChartData = {
    labels: data.payment_types.map((pt) => pt.payment_type_name),
    datasets: [
      {
        label: "Total Amount",
        data: data.payment_types.map((pt) => pt.total_amount),
        backgroundColor: "#36A2EB",
      },
    ],
  };

  return (
    <div className="page-body">
      <Container className="mt-4">
        <h2 className="text-center mb-4">Payment Type Report</h2>

        {/* Date Range Filter */}
        <Card className="mb-4">
          <CardBody>
            <Row>
              <Col md={4}>
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, start: e.target.value })
                  }
                />
              </Col>
              <Col md={4}>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, end: e.target.value })
                  }
                />
              </Col>
              <Col md={4}>
                <Button
                  color="primary"
                  onClick={fetchPaymentTypeReport}
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Fetch Report"}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Summary Cards */}
        {data.summary && (
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h5>Total Sales</h5>
                  <h3>{data.summary.total_sales}</h3>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h5>Total Amount</h5>
                  <h3>{getFormattedPrice(data.summary.total_amount)}</h3>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h5>Total Paid</h5>
                  <h3>{getFormattedPrice(data.summary.total_paid)}</h3>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h5>Transactions</h5>
                  <h3>{data.summary.total_transactions}</h3>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Charts */}
        {data.payment_types.length > 0 && (
          <Row className="mb-4">
            <Col md={6}>
              <Card>
                <CardBody>
                  <CardTitle tag="h5">Payment Types Distribution</CardTitle>
                  <Pie data={pieChartData} />
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card>
                <CardBody>
                  <CardTitle tag="h5">Payment Types Amount</CardTitle>
                  <Bar data={barChartData} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Report Note */}
        {data.payment_types.some(
          (pt) => pt.payment_type_name === "Unknown Type"
        ) && (
          <Card className="mb-4 border-warning">
            <CardBody className="bg-warning-light">
              <h6 className="text-warning mb-2">⚠️ Report Note</h6>
              <p className="mb-0 text-muted">
                "Unknown Type" indicates that some payment types have been
                removed or are missing from the system. This may affect the
                accuracy of payment type reporting.
              </p>
            </CardBody>
          </Card>
        )}

        {/* Data Table */}
        <Card>
          <CardBody>
            <CardTitle tag="h5">Payment Type Details</CardTitle>
            <DataTable
              columns={columns}
              data={data.payment_types}
              pagination
              striped
              highlightOnHover
              progressPending={loading}
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default PaymentType;
